import React, { useState, useEffect } from 'react';
import './TermsOfService.css';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

const TermsOfService = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const sections = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'definitions', title: 'Definitions' },
    { id: 'acceptance', title: 'Acceptance of Terms' },
    { id: 'use', title: 'Use of Service' },
    { id: 'user-accounts', title: 'User Accounts' },
    { id: 'privacy', title: 'Privacy Policy' },
    { id: 'intellectual-property', title: 'Intellectual Property' },
    { id: 'prohibited-uses', title: 'Prohibited Uses' },
    { id: 'termination', title: 'Termination' },
    { id: 'disclaimers', title: 'Disclaimers' },
    { id: 'limitation', title: 'Limitation of Liability' },
    { id: 'governing-law', title: 'Governing Law' },
    { id: 'changes', title: 'Changes to Terms' },
    { id: 'contact', title: 'Contact Information' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const handleScroll = () => {
    const scrollPosition = window.scrollY + 100;
    
    for (let i = sections.length - 1; i >= 0; i--) {
      const section = document.getElementById(sections[i].id);
      if (section && section.offsetTop <= scrollPosition) {
        setActiveSection(sections[i].id);
        break;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`tos-container ${isVisible ? 'visible' : ''}`}>
      <Navbar />
      {/* Hero Section */}
      <div className="tos-hero">
        <div className="tos-hero-content">
          <h1 className="tos-hero-title">
            <span className="tos-title-word">Terms</span>
            <span className="tos-title-word">of</span>
            <span className="tos-title-word">Service</span>
          </h1>
          <p className="tos-hero-subtitle">
            Please read these terms carefully before using our service
          </p>
          <div className="tos-hero-decoration">
            <div className="tos-decoration-circle"></div>
            <div className="tos-decoration-line"></div>
            <div className="tos-decoration-circle"></div>
          </div>
        </div>
      </div>

      <div className="tos-content-wrapper">
        {/* Table of Contents */}
        <div className="tos-table-of-contents">
          <h3>Table of Contents</h3>
          <nav className="tos-toc-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`tos-toc-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="tos-content">
          <div className="tos-content-card">
            <section id="introduction" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">01</span>
                Introduction
              </h2>
              <div className="tos-section-content">
                <p>
                  Welcome to WeMeetTo! These Terms of Service ("Terms") govern your use of our 
                  platform and services. By accessing or using WeMeetTo, you agree to be bound 
                  by these Terms and our Privacy Policy.
                </p>
                <p>
                  WeMeetTo is a platform designed to connect people and facilitate meaningful 
                  interactions. We are committed to providing a safe, respectful, and engaging 
                  environment for all users.
                </p>
              </div>
            </section>

            <section id="definitions" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">02</span>
                Definitions
              </h2>
              <div className="tos-section-content">
                <div className="definition-list">
                  <div className="definition-item">
                    <strong>"WeMeetTo"</strong> refers to our platform, website, and all related services.
                  </div>
                  <div className="definition-item">
                    <strong>"User"</strong> means any individual who accesses or uses our platform.
                  </div>
                  <div className="definition-item">
                    <strong>"Content"</strong> includes all text, images, videos, and other materials shared on our platform.
                  </div>
                  <div className="definition-item">
                    <strong>"Service"</strong> refers to all features, functionality, and services provided by WeMeetTo.
                  </div>
                </div>
              </div>
            </section>

            <section id="acceptance" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">03</span>
                Acceptance of Terms
              </h2>
              <div className="tos-section-content">
                <p>
                  By creating an account, accessing our platform, or using any of our services, 
                  you acknowledge that you have read, understood, and agree to be bound by these Terms.
                </p>
                <div className="highlight-box">
                  <p>
                    <strong>Important:</strong> If you do not agree to these Terms, you must not 
                    use our service. Your continued use of WeMeetTo constitutes acceptance of 
                    any modifications to these Terms.
                  </p>
                </div>
              </div>
            </section>

            <section id="use" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">04</span>
                Use of Service
              </h2>
              <div className="tos-section-content">
                <p>You may use WeMeetTo for lawful purposes only. You agree to:</p>
                <ul className="styled-list">
                  <li>Provide accurate and complete information when creating your account</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Use the service in compliance with all applicable laws and regulations</li>
                  <li>Respect the rights and privacy of other users</li>
                  <li>Report any violations or inappropriate behavior</li>
                </ul>
              </div>
            </section>

            <section id="user-accounts" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">05</span>
                User Accounts
              </h2>
              <div className="tos-section-content">
                <p>
                  To access certain features of WeMeetTo, you may need to create an account. 
                  You are responsible for:
                </p>
                <div className="info-grid">
                  <div className="info-card">
                    <div className="info-icon">🔐</div>
                    <h4>Account Security</h4>
                    <p>Keeping your login credentials secure and confidential</p>
                  </div>
                  <div className="info-card">
                    <div className="info-icon">📝</div>
                    <h4>Accurate Information</h4>
                    <p>Providing truthful and up-to-date personal information</p>
                  </div>
                  <div className="info-card">
                    <div className="info-icon">⚡</div>
                    <h4>Account Activity</h4>
                    <p>All activities that occur under your account</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="privacy" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">06</span>
                Privacy Policy
              </h2>
              <div className="tos-section-content">
                <p>
                  Your privacy is important to us. Our Privacy Policy explains how we collect, 
                  use, and protect your information when you use our service.
                </p>
                <p>
                  By using WeMeetTo, you consent to the collection and use of information in 
                  accordance with our Privacy Policy.
                </p>
              </div>
            </section>

            <section id="intellectual-property" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">07</span>
                Intellectual Property
              </h2>
              <div className="tos-section-content">
                <p>
                  WeMeetTo and its original content, features, and functionality are owned by 
                  us and are protected by international copyright, trademark, patent, trade 
                  secret, and other intellectual property laws.
                </p>
                <p>
                  You retain ownership of content you create and share, but grant us a license 
                  to use, display, and distribute such content in connection with our service.
                </p>
              </div>
            </section>

            <section id="prohibited-uses" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">08</span>
                Prohibited Uses
              </h2>
              <div className="tos-section-content">
                <p>You may not use WeMeetTo:</p>
                <div className="prohibited-list">
                  <div className="prohibited-item">
                    <span className="prohibited-icon">❌</span>
                    <span>For any unlawful purpose or to solicit others to perform unlawful acts</span>
                  </div>
                  <div className="prohibited-item">
                    <span className="prohibited-icon">❌</span>
                    <span>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</span>
                  </div>
                  <div className="prohibited-item">
                    <span className="prohibited-icon">❌</span>
                    <span>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</span>
                  </div>
                  <div className="prohibited-item">
                    <span className="prohibited-icon">❌</span>
                    <span>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</span>
                  </div>
                  <div className="prohibited-item">
                    <span className="prohibited-icon">❌</span>
                    <span>To submit false or misleading information</span>
                  </div>
                </div>
              </div>
            </section>

            <section id="termination" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">09</span>
                Termination
              </h2>
              <div className="tos-section-content">
                <p>
                  We may terminate or suspend your account and bar access to the service 
                  immediately, without prior notice or liability, under our sole discretion, 
                  for any reason whatsoever, including without limitation if you breach the Terms.
                </p>
                <p>
                  You may also terminate your account at any time by contacting us or using 
                  the account deletion feature in your settings.
                </p>
              </div>
            </section>

            <section id="disclaimers" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">10</span>
                Disclaimers
              </h2>
              <div className="tos-section-content">
                <p>
                  The information on this platform is provided on an "as is" basis. To the 
                  fullest extent permitted by law, we exclude all representations, warranties, 
                  conditions and terms relating to our platform and the use of this platform.
                </p>
              </div>
            </section>

            <section id="limitation" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">11</span>
                Limitation of Liability
              </h2>
              <div className="tos-section-content">
                <p>
                  In no event shall WeMeetTo, nor its directors, employees, partners, agents, 
                  suppliers, or affiliates, be liable for any indirect, incidental, special, 
                  consequential, or punitive damages, including without limitation, loss of 
                  profits, data, use, goodwill, or other intangible losses.
                </p>
              </div>
            </section>

            <section id="governing-law" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">12</span>
                Governing Law
              </h2>
              <div className="tos-section-content">
                <p>
                  These Terms shall be interpreted and governed by the laws of the jurisdiction 
                  in which WeMeetTo operates, without regard to its conflict of law provisions.
                </p>
              </div>
            </section>

            <section id="changes" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">13</span>
                Changes to Terms
              </h2>
              <div className="tos-section-content">
                <p>
                  We reserve the right, at our sole discretion, to modify or replace these 
                  Terms at any time. If a revision is material, we will provide at least 
                  30 days notice prior to any new terms taking effect.
                </p>
                <p>
                  Your continued use of the service after any such changes constitutes your 
                  acceptance of the new Terms.
                </p>
              </div>
            </section>

            <section id="contact" className="tos-section">
              <h2 className="tos-section-title">
                <span className="tos-section-number">14</span>
                Contact Information
              </h2>
              <div className="tos-section-content">
                <p>
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="contact-info">
                  <div className="contact-item">
                    <span className="contact-icon">📧</span>
                    <span>Email: legal@wemeetto.com</span>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">🌐</span>
                    <span>Website: www.wemeetto.com</span>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">📅</span>
                    <span>Last Updated: {new Date().toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
